import Gleap from 'gleap'
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAlert, usePromiseEvent, useProducts } from '../../hooks'
import { homeEnterpriseSlice, userSlice } from '../../reducers'
import { useLPQContext } from '../lpq-provider'
import { PersonaliseAlert, UpdatingModal } from './components'
import Lpq from './lpq'

/**
 * UserPreferences component that uses global state from LPQProvider
 * This is the version used globally for URL parameter ?lpq
 */
const UserPreferences = () => {
  const dispatch = useDispatch()
  const profile = useSelector(userSlice.selectors.selectProfile)
  const updateProfile = usePromiseEvent('updateProfile')
  const { enqueueAlert, enqueueMsg } = useAlert()

  // This component will be controlled by LPQProvider
  // The open state and close function come from the provider context
  const { shouldOpenLPQ, closeLPQ, lpqParam } = useLPQContext()
  const { lpqMandatory } = useProducts()

  const [saving, setSaving] = useState(false)

  const shouldShowSkipButton = useMemo(() => {
    if (lpqMandatory) return false
    const lpqCompleted = Boolean(profile?.lpqCompleted)
    const lpqDontShowAgain = Boolean(profile?.lpqDontShowAgain)
    return !lpqCompleted && !lpqDontShowAgain
  }, [lpqMandatory, profile?.lpqCompleted, profile?.lpqDontShowAgain])

  const shouldShowCloseButton = useMemo(() => {
    if (lpqMandatory) return Boolean(profile?.lpqCompleted)
    const lpqCompleted = Boolean(profile?.lpqCompleted)
    const lpqDontShowAgain = Boolean(profile?.lpqDontShowAgain)
    return lpqCompleted || lpqDontShowAgain
  }, [lpqMandatory, profile?.lpqCompleted, profile?.lpqDontShowAgain])

  const savePartialProfile = async (partial) => {
    try {
      const response = await updateProfile({
        partial: {
          ...partial,
          ...(profile?.lpqCompleted ? { lpqCompleted: true } : { lpqCompleted: false })
        },
        type: 'update'
      })
      dispatch(userSlice.actions.setProfile(response))
    } catch (err) {
      enqueueMsg('Unable to update the user profile', 'error')
    }
  }

  const handleSavePartial = (partial) => {
    savePartialProfile(partial)
  }

  const saveProfile = async (partial, type = 'create', from) => {
    const response = await updateProfile({
      partial: {
        ...partial,
        ...(type !== 'cancel' && { lpqCompleted: true }),
        ...(type === 'cancel' && from === 'skip-button' && {
          lpqDontShowAgain: true
        })
      },
      type,
      ...(from && { from })
    })
    dispatch(userSlice.actions.setProfile(response))
  }

  const handleFinishLpq = async (partial) => {
    try {
      setSaving(true)
      // Save profile first to ensure Redux state updates before closing modal
      await saveProfile(partial)
      // Ensures "Because you are interested in" section shows correct topics
      dispatch(homeEnterpriseSlice.actions.resetLoading())
      // Close LPQ after profile is saved
      closeLPQ()

      const isFirstTimeCompletion = !profile?.lpqCompleted
      if (isFirstTimeCompletion) {
        try {
          Gleap.trackEvent('LPQ Completed', {
            pk: profile?.pk,
            sk: profile?.sk
          })
        } catch (gleapError) {
          console.warn('Failed to track LPQ completion event:', gleapError)
        }
      }

      enqueueAlert(<PersonaliseAlert />)
    } catch (err) {
      enqueueMsg('Unable to update the user profile', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = async (from) => {
    try {
      // Save profile FIRST before closing
      await saveProfile({}, 'cancel', from)
      closeLPQ()
    } catch (err) {
      enqueueMsg('Unable to update the user profile', 'error')
    }
  }

  const handleCloseWithoutSave = () => {
    closeLPQ()
  }

  if (!shouldOpenLPQ) {
    return null
  }

  return (
    <>
      <Lpq
        profile={profile}
        onClose={handleClose}
        onCloseWithoutSave={handleCloseWithoutSave}
        lpqParam={lpqParam}
        onFinish={handleFinishLpq}
        onSavePartial={handleSavePartial}
        mandatory={lpqMandatory}
        showSkipButton={shouldShowSkipButton}
        showCloseButton={shouldShowCloseButton}
      />
      {saving && <UpdatingModal open={saving} />}
    </>
  )
}

export default UserPreferences
